import BrandingService from '../services/branding';
import { getKey, getMostSpecificId } from '../utils/hierarchy';
import { getHexColor, sortColors } from '../utils/color';
import Event from '../utils/event';
import { v4 as uuid } from 'uuid';

function ColorStore( { auth, themeId, solutionId, experienceId, programId } ) {
    const key = getKey( { auth, themeId, solutionId, experienceId, programId } );
    const brandingService = new BrandingService( auth.state );
    const colorsChange = new Event();

    let getColorsPromise = null;
    let saveColorsPromise = null;

    function getColors() {
        if ( !getColorsPromise ) {
            const companyId = auth.state.claims.company_id;
            const options = { themeId, solutionId, experienceId, programId };
            const requestedPaletteType = getMostSpecificId( companyId, options );

            getColorsPromise = new Promise( ( resolve, reject ) => {
                brandingService.getColors( options )
                    .then( ( { colors } ) => {
                        // get if the actual palette type
                        const actualPaletteType = (
                            colors.length > 0 ? colors[ 0 ].paletteType : requestedPaletteType
                        );
                        colors.forEach( color => {
                            // make sure if the requested themeId is null if the themeId on the color isn't undefined
                            if ( themeId === null && !color.themeId ) {
                                color.themeId = null;
                            }
                            // if the colors we requested don't match, then we need assign new ids and hierarchy values
                            if ( actualPaletteType !== requestedPaletteType || color.themeId !== themeId ) {
                                color.isCopy = true;
                                color.id = uuid();
                                color.themeId = themeId;
                                color.solutionId = solutionId;
                                color.experienceId = experienceId;
                                color.programId = programId;
                            }
                        } );
                        resolve( {
                            colors: sortColors( colors ),
                            requestedPaletteType,
                            actualPaletteType
                        } );
                    } )
                    .catch ( ex => {
                        reject( ex );
                        getColorsPromise = null;
                    } );
            } );
        }

        return getColorsPromise;
    }
    function saveColors() {
        if ( !saveColorsPromise ) {
            saveColorsPromise = new Promise( ( resolve, reject ) => {
                return getColors().then( state => {
                    brandingService.upsertColors(
                        state.colors.filter( color => {
                            // if the color was new or a copy and it doesn't have a value (deleted)
                            // don't try to save those because they don't exist yet
                            if ( ( color.isNew || color.isCopy ) && !color.hex ) {
                                return false;
                            }
                            return true;
                        } )
                    )
                        .then( ( { colors } ) => {
                            const newState = {
                                ...state,
                                colors: sortColors( colors ),
                                actualPaletteType: state.requestedPaletteType
                            };
                            getColorsPromise = new Promise( r => r( newState ) );
                            resolve( newState );
                            colorsChange.fire( newState );
                            saveColorsPromise = null;
                        } )
                        .catch ( ex => {
                            reject( ex );
                            saveColorsPromise = null;
                        } );
                } );
            } );
        }
        return saveColorsPromise;
    }
    function addColor( value ) {
        const hex = getHexColor( value );
        const companyId = auth.state.claims.company_id;

        return getColors().then( state => {
            getColorsPromise = new Promise( ( resolve ) => {
                // check to see if the color already exists
                const index = state.colors.findIndex( c => c.hex === hex );
                if ( index >= 0 ) {
                    return resolve( state );
                }
                const newState = {
                    ...state,
                    colors: sortColors( [
                        ...state.colors,
                        {
                            isNew: true,
                            id: uuid(),
                            hex,
                            themeId,
                            companyId,
                            solutionId,
                            experienceId,
                            programId
                        }
                    ] )
                };
                resolve( newState );
                colorsChange.fire( newState );
            } );
        } );
    }
    function removeColor( colorId ) {
        return getColors().then( state => {
            getColorsPromise = new Promise( ( resolve ) => {
                const newState = {
                    ...state,
                    colors: state.colors.map( c => {
                        if ( c.id !== colorId ) {
                            return c;
                        }
                        return {
                            ...c,
                            hex: ''
                        };
                    } )
                };
                resolve( newState );
                colorsChange.fire( newState );
            } );
        } );
    }

    return {
        key,
        colorsChange,
        getColors,
        saveColors,
        addColor,
        removeColor
    };
}

// need to keep a mapping to seperate companyId, theme, solution, experience, program combinations
const storeMap = {};

export default function( props ) {
    const storeKey = getKey( props );
    if ( !storeMap[ storeKey ] ) {
        storeMap[ storeKey ] = new ColorStore( props );
    }
    return storeMap[ storeKey ];
}
